/**
 * The contact-detail composer's draft slot, scoped to ONE (team, contact). Shared by the dashboard
 * (`ContactDetailPageNextJs`) and the extension side panel: both autosave the rep's in-progress step here and seeds the composer from it when it opens with
 * `?action=<type>`; other surfaces (the post-call "Use draft") write it and then open the composer,
 * which is how they hand the rep a prefilled draft — nothing is sent until the rep presses Send.
 *
 * Scoping is the point: the old single `savedStep` key was shared by every contact, so a draft
 * written for Jane appeared in Bob's composer. A draft is only ever read back for the contact it
 * was written for. Every access is best-effort — storage can be unavailable (private mode, quota).
 */
import { composerBodyToPlainText } from './sendErrors';

export type ComposerDraftScope = { teamId: string; contactId: string };

/** What the composer restores: the HTML body, and the subject when one was saved. */
export type RestorableComposerDraft = { body2: string; subject?: string | null };

/** The pre-scoping key. Never read — removed on sight so an old unscoped draft can't leak. */
const LEGACY_UNSCOPED_KEY = 'savedStep';

export function composerDraftKey(scope: ComposerDraftScope): string {
  return `composerDraft:${scope.teamId}:${scope.contactId}`;
}

/** The side panel and the dashboard both have `localStorage`; a worker or SSR pass has none. */
function storage(): Storage | null {
  try {
    return typeof globalThis.localStorage === 'undefined' ? null : globalThis.localStorage;
  } catch {
    return null;
  }
}

function isUsableScope(scope: ComposerDraftScope): boolean {
  return scope.teamId !== '' && scope.contactId !== '';
}

export function writeComposerDraft(scope: ComposerDraftScope, draft: Record<string, unknown>): void {
  if (!isUsableScope(scope)) return;
  try {
    storage()?.setItem(composerDraftKey(scope), JSON.stringify(draft));
  } catch {
    // Quota / private mode: the composer simply won't restore.
  }
}

/** The draft saved for exactly this contact, parsed, or null. */
export function readComposerDraft(scope: ComposerDraftScope): unknown {
  if (!isUsableScope(scope)) return null;
  const s = storage();
  try {
    s?.removeItem(LEGACY_UNSCOPED_KEY);
    const raw = s?.getItem(composerDraftKey(scope));
    return raw != null ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearComposerDraft(scope: ComposerDraftScope): void {
  if (!isUsableScope(scope)) return;
  try {
    storage()?.removeItem(composerDraftKey(scope));
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

/**
 * The draft for this contact that is worth restoring: one whose body has actual words. An emptied
 * editor autosaves `<p></p>`, which is not a draft. One rule, so the dashboard and the extension
 * agree on when the "Draft restored" pill shows.
 */
export function readRestorableComposerDraft(scope: ComposerDraftScope): RestorableComposerDraft | null {
  const draft = readComposerDraft(scope);
  if (typeof draft !== 'object' || draft == null) return null;
  const body2 = (draft as { body2?: unknown }).body2;
  if (typeof body2 !== 'string' || composerBodyToPlainText(body2) === '') return null;
  const subject = (draft as { subject?: unknown }).subject;
  return { body2, subject: typeof subject === 'string' ? subject : null };
}

/** Prefill this contact's composer for its next open. `body2` is the composer's HTML. */
export function seedComposerDraft(scope: ComposerDraftScope, draft: { subject: string; body2: string }): void {
  writeComposerDraft(scope, draft);
}
